import Phaser from 'phaser';
import { App } from './App';
import { EmailReaderModal } from './EmailReaderModal';
import { COLORS, DISPUTE_FORM_MODAL, FONTS, INBOX_WINDOW, TASKBAR, toHexColor } from '../config/gameConfig';
import { COPY } from '../content/copy';
import type { BadgePayload, Email, EventBus, EventPayloads } from '../systems/EventBus';
import type { Tooltip } from '../ui/Tooltip';
import { Scrollable } from '../ui/Scrollable';

type InboxTab = 'inbox' | 'sent' | 'archive';

export class InboxApp extends App {
  private emails: Email[] = [];
  private activeTab: InboxTab = 'inbox';
  private tabContainer!: Phaser.GameObjects.Container;
  private listContainer!: Phaser.GameObjects.Container;
  private scrollable!: Scrollable;
  private currentModal: EmailReaderModal | null = null;
  private pulseActive = false;
  private pulseTimer: Phaser.Time.TimerEvent | null = null;
  private readonly onEmailReceived = (p: { email: Email }) => {
    this.emails = [p.email, ...this.emails];
    this.rerender();
    this.bus.emit('inbox:unread-changed', { count: this.unreadCount });
    this.triggerPulse();
  };

  private readonly onDisputeResolved = (p: { emailId: string }) => {
    const match = this.emails.find((e) => e.id === p.emailId);
    if (!match) return;
    match.loggedAsDispute = true;
    this.rerender();
  };

  private readonly onEmailActionTaken = (p: EventPayloads['email:action-taken']) => {
    const match = this.emails.find((e) => e.id === p.emailId);
    if (!match) return;
    // Every action handles the email — it leaves the Inbox tab and lands in
    // Archive. Each action's footprint differs only in the badge it leaves.
    match.archived = true;
    if (p.action === 'reply') {
      match.repliedTo = true;
      if (p.replyBody) match.replyBody = p.replyBody;
    } else if (p.action === 'forward-collections') {
      match.forwardedToCollections = true;
      this.bus.emit('slack:message-received', {
        message: {
          id: `marcus-collections-${match.id}`,
          author: 'marcus',
          body: `got it — i'll handle ${match.from}. send me a note if anything else lands from them.`,
          fictionalMinutes: 0,
        },
      });
    }
    this.rerender();
    this.bus.emit('inbox:unread-changed', { count: this.unreadCount });
    this.emitBadge();
  };

  constructor(scene: Phaser.Scene, bus: EventBus, seedEmails: Email[], private readonly tooltip?: Tooltip) {
    super(scene, bus, {
      x: INBOX_WINDOW.x,
      y: INBOX_WINDOW.y,
      width: INBOX_WINDOW.width,
      height: INBOX_WINDOW.height,
      title: COPY.inbox.title,
      windowId: 'inbox',
      titleBarHeight: INBOX_WINDOW.titleBarHeight,
    });
    this.emails = [...seedEmails];
    this.renderBody();
    this.bus.on('email:received', this.onEmailReceived);
    this.bus.on('dispute:resolved', this.onDisputeResolved);
    this.bus.on('email:action-taken', this.onEmailActionTaken);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());

    this.bus.emit('inbox:unread-changed', { count: this.unreadCount });
    this.emitBadge();
  }

  get unreadCount(): number {
    return this.emails.filter((e) => !e.read && !e.archived).length;
  }

  private get visibleEmails(): Email[] {
    if (this.activeTab === 'archive') return this.emails.filter((e) => e.archived === true);
    if (this.activeTab === 'sent') return this.emails.filter((e) => e.repliedTo === true);
    return this.emails.filter((e) => e.archived !== true);
  }

  /** Public access for EndingScene beat 3 ("tomorrow's queue reveal"). */
  get allEmails(): readonly Email[] {
    return this.emails;
  }

  getBadge(): BadgePayload {
    return { label: 'Inbox', count: this.unreadCount, pulse: this.pulseActive, iconColor: COLORS.emailUnreadDot };
  }

  private emitBadge(): void {
    this.bus.emit('window:badge-changed', { windowId: this.options.windowId, badge: this.getBadge() });
  }

  private triggerPulse(): void {
    this.pulseActive = true;
    this.emitBadge();
    if (this.pulseTimer) this.pulseTimer.remove();
    this.pulseTimer = this.scene.time.delayedCall(TASKBAR.pulseHoldAfterEventMs, () => {
      this.pulseActive = false;
      this.pulseTimer = null;
      this.emitBadge();
    });
  }

  protected renderBody(): void {
    this.tabContainer = this.scene.add.container(0, 0);
    this.bodyContainer.add(this.tabContainer);

    this.listContainer = this.scene.add.container(0, INBOX_WINDOW.tabBarHeight);
    this.bodyContainer.add(this.listContainer);
    this.scrollable = new Scrollable(this.scene, this, this.listContainer, this.bodyContainer, {
      viewportYOffset: INBOX_WINDOW.tabBarHeight,
      viewportHeight: this.bodyHeight - INBOX_WINDOW.tabBarHeight,
    });
    this.rerender();
  }

  protected onResize(): void {
    this.scrollable?.resize(this.bodyWidth, this.bodyHeight - INBOX_WINDOW.tabBarHeight);
    this.rerender();
  }

  private renderTabs(): void {
    this.tabContainer.removeAll(true);
    const inboxCount = this.emails.filter((e) => !e.archived).length;
    const archiveCount = this.emails.filter((e) => e.archived).length;
    const sentCount = this.emails.filter((e) => e.repliedTo).length;
    const tabs: { id: InboxTab; label: string }[] = [
      { id: 'inbox', label: `Inbox (${inboxCount})` },
      { id: 'sent', label: `Sent (${sentCount})` },
      { id: 'archive', label: `Archive (${archiveCount})` },
    ];
    let x = INBOX_WINDOW.tabPaddingX;
    for (const t of tabs) {
      const isActive = t.id === this.activeTab;
      const label = this.scene.add.text(
        x,
        INBOX_WINDOW.tabBarHeight / 2,
        t.label,
        {
          fontFamily: FONTS.ui,
          fontSize: INBOX_WINDOW.tabFontSize,
          color: toHexColor(isActive ? COLORS.textPrimary : COLORS.textMuted),
          fontStyle: isActive ? 'bold' : 'normal',
        },
      ).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
      label.on('pointerdown', () => {
        if (this.activeTab === t.id) return;
        this.activeTab = t.id;
        this.scrollable?.applyDelta(-Infinity);
        this.rerender();
      });
      this.tabContainer.add(label);
      if (isActive) {
        const underline = this.scene.add
          .rectangle(x, INBOX_WINDOW.tabBarHeight - 2, label.width, 2, COLORS.markedTermUnderline)
          .setOrigin(0, 0);
        this.tabContainer.add(underline);
      }
      x += label.width + INBOX_WINDOW.tabGap;
    }
    const divider = this.scene.add
      .rectangle(0, INBOX_WINDOW.tabBarHeight - 1, this.bodyWidth, 1, COLORS.emailSeparator)
      .setOrigin(0, 0);
    this.tabContainer.add(divider);
  }

  private rerender(): void {
    if (!this.listContainer) return;
    this.renderTabs();
    this.listContainer.removeAll(true);

    const list = this.visibleEmails;

    if (list.length === 0) {
      const placeholder = this.scene.add.text(
        this.bodyWidth / 2,
        24,
        this.activeTab === 'archive' ? 'Nothing archived yet.' : 'Inbox empty.',
        {
          fontFamily: FONTS.ui,
          fontSize: '12px',
          color: toHexColor(COLORS.textMuted),
          fontStyle: 'italic',
        },
      ).setOrigin(0.5, 0);
      this.listContainer.add(placeholder);
      this.scrollable?.setContentHeight(48);
      return;
    }

    list.forEach((email, i) => {
      const y = i * INBOX_WINDOW.emailRowHeight;

      // Clickable row hit zone (full-width rectangle, invisible fill).
      const hit = this.scene.add
        .rectangle(0, y, this.bodyWidth, INBOX_WINDOW.emailRowHeight, 0x000000, 0)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        // Phaser's mask clips visuals but not input — a row scrolled above
        // the body viewport (under the title bar / tab bar) is still
        // hit-tested. Ignore the click unless it's inside the visible body.
        if (!this.scrollable.viewportContainsPointer(pointer.x, pointer.y)) return;
        this.openEmail(email);
      });
      this.listContainer.add(hit);

      const separator = this.scene.add
        .rectangle(0, y, this.bodyWidth, 1, COLORS.emailSeparator)
        .setOrigin(0, 0);
      this.listContainer.add(separator);

      if (!email.read) {
        const dot = this.scene.add.circle(8, y + INBOX_WINDOW.emailRowHeight / 2, 3, COLORS.emailUnreadDot);
        this.listContainer.add(dot);
      }

      // In Sent tab the row shows the reply we sent: "Re: ..." subject and
      // the reply body as preview, addressed To: the original sender.
      const isSentView = this.activeTab === 'sent';
      const subjectText = isSentView
        ? (email.subject.toLowerCase().startsWith('re:') ? email.subject : `Re: ${email.subject}`)
        : email.subject;
      const metaText = isSentView
        ? `To: ${email.from} · ${email.replyBody ?? ''}`
        : `${email.from} · ${email.receivedAt}`;

      const subject = this.scene.add.text(
        INBOX_WINDOW.emailPaddingX,
        y + 10,
        subjectText,
        {
          fontFamily: FONTS.ui,
          fontSize: '13px',
          color: toHexColor(COLORS.emailSubject),
          fontStyle: email.read ? 'normal' : 'bold',
        },
      ).setOrigin(0, 0);
      this.listContainer.add(subject);

      const meta = this.scene.add.text(
        INBOX_WINDOW.emailPaddingX,
        y + 28,
        metaText,
        {
          fontFamily: FONTS.ui,
          fontSize: '11px',
          color: toHexColor(COLORS.emailMeta),
          wordWrap: { width: this.bodyWidth - INBOX_WINDOW.emailPaddingX * 2 },
        },
      ).setOrigin(0, 0);
      this.listContainer.add(meta);

      if (email.forwardedToCollections) {
        const pillX = this.bodyWidth - DISPUTE_FORM_MODAL.inboxBadgeWidth - 8;
        const pillY = y + (INBOX_WINDOW.emailRowHeight - DISPUTE_FORM_MODAL.inboxBadgeHeight) / 2;
        const pill = this.scene.add
          .rectangle(pillX, pillY, DISPUTE_FORM_MODAL.inboxBadgeWidth, DISPUTE_FORM_MODAL.inboxBadgeHeight, COLORS.accentWarn)
          .setOrigin(0, 0);
        this.listContainer.add(pill);
        const pillLabel = this.scene.add.text(
          pillX + DISPUTE_FORM_MODAL.inboxBadgeWidth / 2,
          pillY + DISPUTE_FORM_MODAL.inboxBadgeHeight / 2,
          '→ Collections',
          {
            fontFamily: FONTS.ui,
            fontSize: DISPUTE_FORM_MODAL.inboxBadgeFontSize,
            color: toHexColor(COLORS.windowBody),
            fontStyle: 'bold',
          },
        ).setOrigin(0.5, 0.5);
        this.listContainer.add(pillLabel);
      } else if (email.repliedTo) {
        const pillX = this.bodyWidth - DISPUTE_FORM_MODAL.inboxBadgeWidth - 8;
        const pillY = y + (INBOX_WINDOW.emailRowHeight - DISPUTE_FORM_MODAL.inboxBadgeHeight) / 2;
        const pill = this.scene.add
          .rectangle(pillX, pillY, DISPUTE_FORM_MODAL.inboxBadgeWidth, DISPUTE_FORM_MODAL.inboxBadgeHeight, COLORS.success)
          .setOrigin(0, 0);
        this.listContainer.add(pill);
        const pillLabel = this.scene.add.text(
          pillX + DISPUTE_FORM_MODAL.inboxBadgeWidth / 2,
          pillY + DISPUTE_FORM_MODAL.inboxBadgeHeight / 2,
          '✓ Replied',
          {
            fontFamily: FONTS.ui,
            fontSize: DISPUTE_FORM_MODAL.inboxBadgeFontSize,
            color: toHexColor(COLORS.windowBody),
            fontStyle: 'bold',
          },
        ).setOrigin(0.5, 0.5);
        this.listContainer.add(pillLabel);
      } else if (email.loggedAsDispute) {
        const pillX = this.bodyWidth - DISPUTE_FORM_MODAL.inboxBadgeWidth - 8;
        const pillY = y + (INBOX_WINDOW.emailRowHeight - DISPUTE_FORM_MODAL.inboxBadgeHeight) / 2;
        const pill = this.scene.add
          .rectangle(pillX, pillY, DISPUTE_FORM_MODAL.inboxBadgeWidth, DISPUTE_FORM_MODAL.inboxBadgeHeight, COLORS.badgeLogged)
          .setOrigin(0, 0);
        this.listContainer.add(pill);
        const pillLabel = this.scene.add.text(
          pillX + DISPUTE_FORM_MODAL.inboxBadgeWidth / 2,
          pillY + DISPUTE_FORM_MODAL.inboxBadgeHeight / 2,
          COPY.dispute.readerLoggedBadge,
          {
            fontFamily: FONTS.ui,
            fontSize: DISPUTE_FORM_MODAL.inboxBadgeFontSize,
            color: toHexColor(COLORS.buttonPrimaryText),
            fontStyle: 'bold',
          },
        ).setOrigin(0.5, 0.5);
        this.listContainer.add(pillLabel);
      }
    });

    this.scrollable?.setContentHeight(list.length * INBOX_WINDOW.emailRowHeight + 8);
    this.refreshChildDepths();
  }

  private openEmail(email: Email): void {
    if (this.currentModal) return; // Only one modal at a time.

    const wasUnread = !email.read;
    email.read = true;

    this.currentModal = new EmailReaderModal(this.scene, email, () => {
      this.currentModal = null;
    }, this.bus, this.tooltip);

    if (wasUnread) {
      this.rerender();
      this.bus.emit('inbox:unread-changed', { count: this.unreadCount });
      this.emitBadge();
    }
  }

  private teardown(): void {
    if (this.pulseTimer) {
      this.pulseTimer.remove();
      this.pulseTimer = null;
    }
    this.bus.off('email:received', this.onEmailReceived);
    this.bus.off('dispute:resolved', this.onDisputeResolved);
    this.bus.off('email:action-taken', this.onEmailActionTaken);
    this.scrollable?.destroy();
  }
}
